import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  TrendingUp,
  DollarSign,
  ShoppingCart,
  Percent,
  Calendar,
  CreditCard,
  Banknote,
  Building2,
  RefreshCcw
} from 'lucide-react';
import { getSalesAnalytics } from '../../api/sales';
import { toast } from 'react-toastify';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import PeriodFilter from '../../components/common/PeriodFilter';
import { useFiscalPeriod } from '../../contexts/FiscalPeriodContext';

const SalesAnalytics = () => {
  const navigate = useNavigate();
  const { periodRange } = useFiscalPeriod();
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(false);

  // Local period filter state (null = use global, otherwise override)
  const [localPeriod, setLocalPeriod] = useState(null);

  // Current active date range (local override or global)
  const activeDateRange = useMemo(() => {
    if (localPeriod) {
      return {
        startDate: localPeriod.start_date || '',
        endDate: localPeriod.end_date || '',
        label: localPeriod.label
      };
    }
    return {
      startDate: periodRange?.start_date || '',
      endDate: periodRange?.end_date || '',
      label: periodRange?.period_label || 'All Time'
    };
  }, [localPeriod, periodRange]);

  // Fetch analytics on mount and when date range changes
  const fetchAnalytics = useCallback(async () => {
    setLoading(true);
    try {
      const filters = {};
      if (activeDateRange.startDate) filters.start_date = activeDateRange.startDate;
      if (activeDateRange.endDate) filters.end_date = activeDateRange.endDate;

      const response = await getSalesAnalytics(filters);
      setAnalytics(response.data);
    } catch (error) {
      toast.error('Failed to fetch sales analytics');
    } finally {
      setLoading(false);
    }
  }, [activeDateRange.startDate, activeDateRange.endDate]);

  useEffect(() => {
    fetchAnalytics();
  }, [fetchAnalytics]);

  const handleDateChange = (field, value) => {
    // When user manually changes dates, set as local override
    setLocalPeriod((prev) => ({
      start_date: field === 'startDate' ? value : prev?.start_date || activeDateRange.startDate,
      end_date: field === 'endDate' ? value : prev?.end_date || activeDateRange.endDate,
      label: 'Custom'
    }));
  };

  const clearFilters = () => {
    setLocalPeriod(null); // Reset to global period
  };

  // Handle period filter change
  const handlePeriodChange = useCallback((range) => {
    // If user selects "Global Period" option (isGlobal flag), reset to use global settings
    if (range.isGlobal) {
      setLocalPeriod(null);
    } else {
      setLocalPeriod(range);
    }
  }, []);

  const formatCurrency = (value) => {
    return `₦${parseFloat(value || 0).toLocaleString('en-NG', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const formatNumber = (value) => {
    return parseInt(value || 0).toLocaleString('en-NG');
  };

  const calculateProfitMargin = () => {
    if (!analytics || !analytics.total_revenue || analytics.total_revenue == 0) return 0;
    return ((analytics.total_net_profit / analytics.total_revenue) * 100).toFixed(2);
  };

  const getPaymentMethodBreakdown = () => {
    if (!analytics) return [];

    return [
      {
        name: 'Cash',
        value: parseFloat(analytics.cash_sales || 0),
        icon: Banknote,
        color: 'bg-blue-500',
        textColor: 'text-blue-700',
        bgColor: 'bg-blue-100/15'
      },
      {
        name: 'POS/Card',
        value: parseFloat(analytics.pos_sales || 0),
        icon: CreditCard,
        color: 'bg-purple-500',
        textColor: 'text-purple-700',
        bgColor: 'bg-purple-100/15'
      },
      {
        name: 'Transfer',
        value: parseFloat(analytics.transfer_sales || 0),
        icon: Building2,
        color: 'bg-indigo-500',
        textColor: 'text-indigo-700',
        bgColor: 'bg-indigo-100/15'
      },
      {
        name: 'Credit',
        value: parseFloat(analytics.credit_sales || 0),
        icon: RefreshCcw,
        color: 'bg-orange-500',
        textColor: 'text-orange-700',
        bgColor: 'bg-orange-100/15'
      }
    ];
  };

  return (
    <div className="min-h-screen bg-secondary-offWhite p-3 sm:p-4 md:p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-sm p-4 sm:p-5 md:p-6 mb-4 sm:mb-5 md:mb-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4">
            <div className="flex-1 min-w-0">
              <h1 className="text-xl sm:text-2xl md:text-3xl font-bold text-primary-black truncate">
                Sales Analytics
              </h1>
              <p className="text-xs sm:text-sm text-secondary-gray mt-1 line-clamp-2">
                {activeDateRange.startDate || activeDateRange.endDate
                  ? `Filtered results${activeDateRange.startDate ? ` from ${new Date(activeDateRange.startDate).toLocaleDateString()}` : ''}${activeDateRange.endDate ? ` to ${new Date(activeDateRange.endDate).toLocaleDateString()}` : ''}`
                  : 'All time statistics'}
                {localPeriod && (
                  <span className="ml-2 px-2 py-0.5 bg-blue-100 text-blue-700 text-xs rounded-full">
                    {activeDateRange.label}
                  </span>
                )}
              </p>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <PeriodFilter
                onChange={handlePeriodChange}
                compact={true}
                storageKey="sales_analytics"
              />
              <button
                onClick={() => navigate('/sales')}
                className="px-4 py-2 bg-accent-gold text-white rounded-lg hover:bg-yellow-600 transition-colors text-sm sm:text-base whitespace-nowrap"
              >
                View All Sales
              </button>
            </div>
          </div>
        </div>

        {/* Date Range Filter */}
        <div className="bg-white rounded-lg shadow-sm p-4 sm:p-5 md:p-6 mb-4 sm:mb-5 md:mb-6">
          <div className="flex items-center gap-2 mb-3 sm:mb-4">
            <Calendar className="w-4 h-4 sm:w-5 sm:h-5 text-accent-gold flex-shrink-0" />
            <h2 className="text-base sm:text-lg font-semibold text-primary-black truncate">
              Date Range Filter
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3 sm:gap-4">
            <div>
              <label className="block text-xs sm:text-sm text-secondary-gray mb-1">
                Start Date
              </label>
              <input
                type="date"
                value={activeDateRange.startDate}
                onChange={(e) => handleDateChange('startDate', e.target.value)}
                className="w-full px-3 py-2 text-sm sm:text-base border border-secondary-lightGray rounded-lg focus:outline-none focus:ring-2 focus:ring-accent-gold"
              />
            </div>
            <div>
              <label className="block text-xs sm:text-sm text-secondary-gray mb-1">End Date</label>
              <input
                type="date"
                value={activeDateRange.endDate}
                onChange={(e) => handleDateChange('endDate', e.target.value)}
                className="w-full px-3 py-2 text-sm sm:text-base border border-secondary-lightGray rounded-lg focus:outline-none focus:ring-2 focus:ring-accent-gold"
              />
            </div>
            <div className="flex items-end sm:col-span-2 md:col-span-1">
              <button
                onClick={clearFilters}
                className="w-full px-4 py-2 text-sm sm:text-base border border-secondary-lightGray rounded-lg text-secondary-gray hover:bg-secondary-offWhite transition-colors"
              >
                Clear Filters
              </button>
            </div>
          </div>
        </div>

        {loading ? (
          <LoadingSpinner message="Loading sales analytics..." className="py-12" />
        ) : analytics ? (
          <>
            {/* Key Metrics Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 md:gap-6 mb-4 sm:mb-5 md:mb-6">
              {/* Total Sales */}
              <div className="bg-white rounded-lg shadow-sm p-4 sm:p-5 md:p-6">
                <div className="flex items-center justify-between mb-2 sm:mb-3">
                  <div className="p-2 sm:p-3 bg-blue-50 rounded-lg">
                    <ShoppingCart className="w-5 h-5 sm:w-6 sm:h-6 text-blue-600" />
                  </div>
                </div>
                <p className="text-xs sm:text-sm text-secondary-gray mb-1">Total Sales</p>
                <h3 className="text-xl sm:text-2xl md:text-3xl font-bold text-primary-black truncate">
                  {formatNumber(analytics.total_sales)}
                </h3>
              </div>

              {/* Total Revenue */}
              <div className="bg-white rounded-lg shadow-sm p-4 sm:p-5 md:p-6">
                <div className="flex items-center justify-between mb-2 sm:mb-3">
                  <div className="p-2 sm:p-3 bg-green-50 rounded-lg">
                    <DollarSign className="w-5 h-5 sm:w-6 sm:h-6 text-green-600" />
                  </div>
                </div>
                <p className="text-xs sm:text-sm text-secondary-gray mb-1">Total Revenue</p>
                <h3 className="text-lg sm:text-xl md:text-2xl font-bold text-green-600 truncate">
                  {formatCurrency(analytics.total_revenue)}
                </h3>
              </div>

              {/* Gross Profit */}
              <div className="bg-white rounded-lg shadow-sm p-4 sm:p-5 md:p-6">
                <div className="flex items-center justify-between mb-2 sm:mb-3">
                  <div className="p-2 sm:p-3 bg-yellow-50 rounded-lg">
                    <TrendingUp className="w-5 h-5 sm:w-6 sm:h-6 text-yellow-600" />
                  </div>
                </div>
                <p className="text-xs sm:text-sm text-secondary-gray mb-1">
                  {parseFloat(analytics.total_gross_profit) >= 0 ? 'Gross Profit' : 'Gross Loss'}
                </p>
                <h3
                  className={`text-lg sm:text-xl md:text-2xl font-bold ${parseFloat(analytics.total_gross_profit) >= 0 ? 'text-accent-gold' : 'text-red-600'} truncate`}
                >
                  {formatCurrency(analytics.total_gross_profit)}
                </h3>
              </div>

              {/* Net Profit */}
              <div className="bg-white rounded-lg shadow-sm p-4 sm:p-5 md:p-6">
                <div className="flex items-center justify-between mb-2 sm:mb-3">
                  <div className="p-2 sm:p-3 bg-purple-50 rounded-lg">
                    <Percent className="w-5 h-5 sm:w-6 sm:h-6 text-purple-600" />
                  </div>
                </div>
                <p className="text-xs sm:text-sm text-secondary-gray mb-1">
                  {parseFloat(analytics.total_net_profit) >= 0 ? 'Net Profit' : 'Net Loss'}
                </p>
                <h3
                  className={`text-lg sm:text-xl md:text-2xl font-bold ${parseFloat(analytics.total_net_profit) >= 0 ? 'text-purple-600' : 'text-red-600'} truncate`}
                >
                  {formatCurrency(analytics.total_net_profit)}
                </h3>
                <p className="text-xs sm:text-sm text-secondary-gray mt-1 truncate">
                  Margin: {calculateProfitMargin()}%
                </p>
              </div>
            </div>

            {/* Additional Metrics */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 sm:gap-4 md:gap-6 mb-4 sm:mb-5 md:mb-6">
              {/* Average Sale */}
              <div className="bg-white rounded-lg shadow-sm p-4 sm:p-5 md:p-6">
                <h3 className="text-base sm:text-lg font-semibold text-primary-black mb-3 sm:mb-4 truncate">
                  Average Sale
                </h3>
                <div className="flex items-center justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <p className="text-xl sm:text-2xl md:text-3xl font-bold text-accent-gold truncate">
                      {formatCurrency(analytics.average_sale_amount)}
                    </p>
                    <p className="text-xs sm:text-sm text-secondary-gray mt-1">Per transaction</p>
                  </div>
                  <div className="p-3 sm:p-4 bg-yellow-50 rounded-lg flex-shrink-0">
                    <DollarSign className="w-6 h-6 sm:w-7 sm:h-7 md:w-8 md:h-8 text-accent-gold" />
                  </div>
                </div>
              </div>

              {/* Profit Overview */}
              <div className="bg-white rounded-lg shadow-sm p-4 sm:p-5 md:p-6">
                <h3 className="text-base sm:text-lg font-semibold text-primary-black mb-3 sm:mb-4 truncate">
                  Profit Overview
                </h3>
                <div className="space-y-2 sm:space-y-3">
                  <div className="flex justify-between items-center gap-3">
                    <span className="text-xs sm:text-sm text-secondary-gray">Revenue</span>
                    <span className="font-semibold text-primary-black text-xs sm:text-sm truncate">
                      {formatCurrency(analytics.total_revenue)}
                    </span>
                  </div>
                  <div className="flex justify-between items-center gap-3">
                    <span className="text-xs sm:text-sm text-secondary-gray">
                      {parseFloat(analytics.total_gross_profit) >= 0
                        ? 'Gross Profit'
                        : 'Gross Loss'}
                    </span>
                    <span
                      className={`font-semibold ${parseFloat(analytics.total_gross_profit) >= 0 ? 'text-accent-gold' : 'text-red-600'} text-xs sm:text-sm truncate`}
                    >
                      {formatCurrency(analytics.total_gross_profit)}
                    </span>
                  </div>
                  <div className="flex justify-between items-center gap-3 pt-2 sm:pt-3 border-t border-secondary-lightGray">
                    <span className="text-xs sm:text-sm font-semibold text-primary-black">
                      {parseFloat(analytics.total_net_profit) >= 0 ? 'Net Profit' : 'Net Loss'}
                    </span>
                    <span
                      className={`font-bold ${parseFloat(analytics.total_net_profit) >= 0 ? 'text-green-600' : 'text-red-600'} text-xs sm:text-sm truncate`}
                    >
                      {formatCurrency(analytics.total_net_profit)}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Payment Methods Breakdown */}
            <div className="bg-white rounded-lg shadow-sm p-4 sm:p-5 md:p-6">
              <h3 className="text-base sm:text-lg font-semibold text-primary-black mb-4 sm:mb-6 truncate">
                Payment Methods Breakdown
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
                {getPaymentMethodBreakdown().map((method, index) => {
                  const Icon = method.icon;
                  const percentage =
                    analytics.total_revenue > 0
                      ? ((method.value / analytics.total_revenue) * 100).toFixed(1)
                      : 0;

                  return (
                    <div
                      key={index}
                      className={`${method.bgColor} rounded-lg p-3 sm:p-4 border border-opacity-20`}
                    >
                      <div className="flex items-center gap-2 sm:gap-3 mb-2 sm:mb-3">
                        <div
                          className={`p-1.5 sm:p-2 ${method.color} bg-opacity-20 rounded-lg flex-shrink-0`}
                        >
                          <Icon
                            className={`w-4 h-4 sm:w-5 sm:h-5 ${method.color.replace('bg-', 'text-')}`}
                          />
                        </div>
                        <h4
                          className={`font-semibold text-sm sm:text-base ${method.textColor} truncate`}
                        >
                          {method.name}
                        </h4>
                      </div>
                      <p
                        className={`text-lg sm:text-xl md:text-2xl font-bold ${method.textColor} mb-1 truncate`}
                      >
                        {formatCurrency(method.value)}
                      </p>
                      <p className="text-xs sm:text-sm text-secondary-gray truncate">
                        {percentage}% of total revenue
                      </p>
                    </div>
                  );
                })}
              </div>
            </div>
          </>
        ) : (
          <div className="bg-white rounded-lg shadow-sm p-12 text-center">
            <p className="text-secondary-gray">No data available</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default SalesAnalytics;
